import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View, } from 'react-native';
import { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import { ModalScrollView } from 'design-system/ModalScrollables';
import { ButtonGroup, rem, Spacer } from 'design-system';
import Button, { HEIGHT as BUTTON_HEIGHT } from 'design-system/Button';
import Text from 'design-system/Text';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useScrollableModalMaxHeight } from 'hooks/useScrollableModalMaxHeight';
import { selectConnections } from 'reducers/selectors';
import { useAppStore } from 'store/hooks';
import Modal from 'utils/packages/Modal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import CreditReportDebtsList from '../components/CreditReportDebtsList';
import CreditReportUntrackedToast from '../components/CreditReportUntrackedToast';
import useCreditReportDebtTracking from './useCreditReportDebtTracking';
const strings = {
    track: {
        title: {
            one: 'Track this account in your net worth',
            other: 'Track these accounts in your net worth',
        },
        description: {
            one: 'We found this account in your credit report and will automatically keep its balance updated in net worth.',
            other: 'We found these accounts in your credit report and will automatically keep their balances updated in net worth.',
        },
        offlineDuplicates: {
            one: 'If you’ve already added it manually as an offline account, delete or close it to avoid counting your debt twice.',
            other: 'If you’ve already added these manually as offline accounts, delete or close them to avoid counting your debt twice.',
        },
        notNow: 'Not now',
        confirm: 'Add to net worth',
    },
    stop: {
        title: {
            one: 'Stop tracking this account in net worth?',
            other: 'Stop tracking these accounts in net worth?',
        },
        description: {
            one: 'This balance will no longer be included in your total net worth calculation. You can turn automatic tracking back on anytime in your settings',
            other: 'These loan and mortgage balances will no longer be included in your total net worth calculation. You can turn automatic tracking back on anytime in your settings',
        },
        keep: 'Keep in net worth',
        confirm: 'Stop tracking',
    },
    offerBeforeOffline: {
        title: {
            one: 'Track this account automatically?',
            other: 'Track these accounts automatically?',
        },
        description: {
            one: 'We found this account in your credit report. We can sync its balance automatically so you don’t need to set up an offline account',
            other: 'We found these accounts in your credit report. We can sync your balance automatically so you don’t need to set up an offline account',
        },
        createOffline: 'Create offline account',
        confirm: 'Track automatically',
    },
    success: {
        title: {
            one: 'Your debt is connected!',
            other: 'Your debts are connected!',
        },
        subTitle: {
            one: 'Your debt is now synced with your credit report. We’ll automatically update your net worth as its balance changes.',
            other: 'Your loans and mortgages are now synced with your credit report. We’ll automatically update your net worth as their balances change.',
        },
        viewNetWorth: 'View net worth',
        done: 'Done',
    },
};
const plural = (debts, { one, other }) => debts.length === 1 ? one : other;
const hasOpenOfflineDebtAccounts = (state) => selectConnections(state).some((connection) => connection.bankInfo.provider === 'MANUAL' &&
    connection.accounts.some((account) => !account.isClosed &&
        (account.type === 'LOAN' || account.type === 'MORTGAGE')));
const BUTTONS_HEIGHT = 2 * BUTTON_HEIGHT + rem(24 + 8);
const DebtTrackingModalContent = ({ descriptions, debts, secondaryButtonTitle, primaryButtonTitle, onPressPrimary, onScroll, }) => {
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => ({ maxHeight }), [maxHeight]);
    const contentContainerStyle = useMarginBottom('paddingBottom', BUTTONS_HEIGHT, styles.content);
    return (<>
      <ModalScrollView style={style} contentContainerStyle={contentContainerStyle} onScroll={onScroll} scrollEventThrottle={16}>
        <View onStartShouldSetResponder={() => true}>
          {descriptions.map((description, index) => (<React.Fragment key={description}>
              {index > 0 ? <Spacer h={10}/> : null}
              <Text TextThin-14 Gray centered>
                {description}
              </Text>
            </React.Fragment>))}
          <CreditReportDebtsList debts={debts}/>
        </View>
      </ModalScrollView>
      <ButtonGroup type="floating" margins={false}>
        <Button brandReversed title={secondaryButtonTitle} onPress={() => Modal.hide()}/>
        <Button brand title={primaryButtonTitle} onPress={() => Modal.hide(onPressPrimary)}/>
      </ButtonGroup>
    </>);
};
const showDebtTrackingModal = ({ title, scrollValue, onScroll, ...contentProps }) => {
    Modal.show(<DebtTrackingModalContent onScroll={onScroll} {...contentProps}/>, {
        title,
        containerStyle: styles.modal,
        scrollable: { scrollValue },
    });
};
const styles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    content: {
        paddingHorizontal: rem(16),
    },
});
export const showStopTrackingDebtsModal = (debts, onConfirm, onScroll, scrollValue) => {
    if (!debts.length)
        return;
    showDebtTrackingModal({
        title: plural(debts, strings.stop.title),
        descriptions: [plural(debts, strings.stop.description)],
        debts,
        secondaryButtonTitle: strings.stop.keep,
        primaryButtonTitle: strings.stop.confirm,
        onPressPrimary: onConfirm,
        onScroll,
        scrollValue,
    });
};
/**
 * The confirmation modals and success screen around tracking credit report
 * debts in net worth.
 */
const useCreditReportDebtTrackingFlow = () => {
    const navigation = useMainStackNavigation();
    const store = useAppStore();
    const setTracked = useCreditReportDebtTracking();
    const scrollValue = useSharedValue(0);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollValue.value = handle.contentOffset.y;
    }, []);
    const showSuccess = useCallback((debts, onDone) => {
        const title = plural(debts, strings.success.title);
        const subTitle = plural(debts, strings.success.subTitle);
        if (onDone) {
            navigation.navigate('Success', {
                title,
                subTitle,
                buttonText: strings.success.done,
                onContinue: () => {
                    navigation.pop();
                    onDone();
                    return true;
                },
            });
            return;
        }
        navigation.navigate('Success', {
            title,
            subTitle,
            buttonText: strings.success.done,
            secondaryButtonText: strings.success.viewNetWorth,
            onSecondaryButtonPress: () => {
                navigation.pop();
                navigation.navigate('AccountsOverview', {
                    title: 'Net Worth - Debt',
                    expandSections: ['LOAN', 'MORTGAGE'],
                });
                return true;
            },
            onContinue: () => {
                navigation.pop();
                return true;
            },
        });
    }, [navigation]);
    /**
     * `onDone` replaces the success screen's "View net worth" button: Done pops
     * back and calls it, for callers already on net worth.
     */
    const startTracking = useCallback((debts, onDone) => {
        if (!debts.length)
            return;
        showDebtTrackingModal({
            title: plural(debts, strings.track.title),
            descriptions: hasOpenOfflineDebtAccounts(store.getState())
                ? [
                    plural(debts, strings.track.description),
                    plural(debts, strings.track.offlineDuplicates),
                ]
                : [plural(debts, strings.track.description)],
            debts,
            secondaryButtonTitle: strings.track.notNow,
            primaryButtonTitle: strings.track.confirm,
            onPressPrimary: async () => {
                const success = await setTracked(debts, true);
                if (!success)
                    return;
                showSuccess(debts, onDone);
            },
            onScroll: scrollHandler,
            scrollValue,
        });
    }, [scrollHandler, scrollValue, setTracked, showSuccess]);
    const stopTracking = useCallback((debts) => {
        showStopTrackingDebtsModal(debts, async () => {
            const success = await setTracked(debts, false);
            if (success)
                CreditReportUntrackedToast.show(debts);
        }, scrollHandler, scrollValue);
    }, [scrollHandler, scrollValue, setTracked]);
    /**
     * Shown when the user starts adding an offline loan or mortgage while the
     * credit report has untracked debts they could track instead.
     */
    const offerTrackingInsteadOfOffline = useCallback((debts, onCreateOffline) => {
        if (!debts.length) {
            onCreateOffline();
            return;
        }
        Modal.showConfirmation({
            title: plural(debts, strings.offerBeforeOffline.title),
            subtitle: (<>
            <Text TextThin-14 Gray centered>
              {plural(debts, strings.offerBeforeOffline.description)}
            </Text>
            <CreditReportDebtsList debts={debts}/>
          </>),
            confirmButtonTitle: strings.offerBeforeOffline.createOffline,
            onConfirm: onCreateOffline,
            cancelButtonTitle: strings.offerBeforeOffline.confirm,
            onPressCancel: async () => {
                const success = await setTracked(debts, true);
                if (success)
                    showSuccess(debts);
            },
        });
    }, [setTracked, showSuccess]);
    return {
        startTracking,
        stopTracking,
        setTracked,
        offerTrackingInsteadOfOffline,
    };
};
export default useCreditReportDebtTrackingFlow;
